import { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Plus, MonitorPlay } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Badge } from '@/shared/components/ui/Badge';
import { Table, type Column } from '@/shared/components/ui/Table';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { useUser } from '@/shared/hooks/useAuth';
import { useUserOptions } from '@/shared/hooks/useUserOptions';
import { useDemos, useDemoMutations } from '../hooks/useDemos';
import { DemoFormModal } from '../components/DemoFormModal';
import { DemoDrawer } from '../components/DemoDrawer';
import {
  DEMO_PRIORITY_OPTIONS,
  DEMO_PRIORITY_TONES,
  DEMO_STATUS_LABELS,
  DEMO_STATUS_OPTIONS,
  DEMO_STATUS_TONES,
  isDemoPrivileged,
} from '../constants';
import type { CreateDemoPayload, Demo, DemosFilters } from '../types';

export function DemosPage() {
  const user = useUser();
  const role = user?.role ?? '';
  const privileged = isDemoPrivileged(role, user?.department);
  const canDelete = ['admin', 'superadmin'].includes(role);

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');

  const filters = useMemo<DemosFilters>(() => {
    const f: DemosFilters = {};
    if (status) f.status = status;
    if (priority) f.priority = priority;
    return f;
  }, [status, priority]);

  const { data: demos = [], isLoading, isError } = useDemos(filters);
  const { data: users = [] } = useUserOptions(privileged);
  const { createDemo } = useDemoMutations();

  const [formOpen, setFormOpen] = useState(false);
  const [drawerId, setDrawerId] = useState<string | null>(null);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return demos;
    return demos.filter((d) =>
      [d.title, d.demo_code, d.client?.name, d.client?.company, d.assignee_name].some((f) => (f ?? '').toLowerCase().includes(q)),
    );
  }, [demos, search]);

  const onCreate = (payload: CreateDemoPayload) => {
    createDemo.mutate(payload, {
      onSuccess: (r) => {
        toast.success(`Demo ${r.demo_code} created.`);
        setFormOpen(false);
      },
      onError: () => toast.error('Failed to create demo.'),
    });
  };

  const columns: Column<Demo>[] = [
    { key: 'demo_code', header: 'Code', render: (d) => <span className="text-body-sm text-on-surface-variant">{d.demo_code || '—'}</span> },
    { key: 'title', header: 'Title', render: (d) => <span className="font-medium text-on-surface">{d.title}</span> },
    { key: 'client', header: 'Client', render: (d) => d.client?.company || d.client?.name || '—' },
    { key: 'status', header: 'Status', render: (d) => <Badge tone={DEMO_STATUS_TONES[d.status] ?? 'neutral'}>{DEMO_STATUS_LABELS[d.status] ?? d.status}</Badge> },
    { key: 'priority', header: 'Priority', render: (d) => (d.priority ? <Badge tone={DEMO_PRIORITY_TONES[d.priority] ?? 'neutral'}>{d.priority}</Badge> : '—') },
    { key: 'assignee', header: 'Assignee', render: (d) => d.assignee_name || '—' },
    { key: 'progress', header: 'Progress', align: 'right', render: (d) => (typeof d.progress === 'number' ? `${d.progress}%` : '—') },
  ];

  return (
    <div className="flex h-full flex-col gap-4 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <MonitorPlay className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-display-sm font-semibold text-on-surface">Project Demos</h1>
            <p className="text-body-sm text-on-surface-variant">{visible.length} demo{visible.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <Button onClick={() => setFormOpen(true)} size="md">
          <Plus className="h-4 w-4" /> New Demo
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="w-64">
          <Input name="demos-search" placeholder="Search title, code, client…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="w-48">
          <Select name="demos-status" value={status} onChange={(e) => setStatus(e.target.value)} options={[{ value: '', label: 'All statuses' }, ...DEMO_STATUS_OPTIONS]} />
        </div>
        <div className="w-44">
          <Select name="demos-priority" value={priority} onChange={(e) => setPriority(e.target.value)} options={[{ value: '', label: 'All priorities' }, ...DEMO_PRIORITY_OPTIONS]} />
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-1 items-center justify-center"><Spinner /></div>
      ) : isError ? (
        <EmptyState title="Failed to load demos" description="Check your connection or try refreshing." />
      ) : visible.length === 0 ? (
        <EmptyState
          title={search || status || priority ? 'No matching demos' : 'No demos yet'}
          description={search || status || priority ? 'Try adjusting your filters.' : 'Create your first demo request.'}
          action={!search && !status && !priority ? <Button onClick={() => setFormOpen(true)}>New Demo</Button> : undefined}
        />
      ) : (
        <div className="flex-1 overflow-y-auto">
          <Table columns={columns} data={visible} rowKey={(d) => d.id} onRowClick={(d) => setDrawerId(d.id)} empty="No demos found." />
        </div>
      )}

      <DemoFormModal
        open={formOpen}
        loading={createDemo.isPending}
        canAssign={privileged}
        users={users}
        onClose={() => setFormOpen(false)}
        onSubmit={onCreate}
      />

      <DemoDrawer
        demoId={drawerId}
        currentUserId={user?.id}
        privileged={privileged}
        canDelete={canDelete}
        users={users}
        onClose={() => setDrawerId(null)}
      />
    </div>
  );
}
